// src/features/radar/radarUtils.js
//
// RADAR-BRIEF-STACK: Date/text helpers for the Decision Brief Stack components.
//
// These are intentional duplicates of the private helpers in LeadershipRadar.jsx.
// They live here so the Pulse-home pane file (LeadershipRadar.jsx) has zero diff,
// satisfying the "Pulse home pane is pixel-identical to before" acceptance criterion.
//
// If the logic ever needs to change, update both files in the same commit.

import { isSaneDate, daysUntilClient } from "../pulse/LeadershipRadar.jsx";
import { T } from "../../app/tokens.js";

export function fmtDue(iso) {
  if (!iso || !isSaneDate(iso)) return "—";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due  = new Date(iso);
  const days = Math.round((due - today) / 86_400_000);
  if (days < 0)   return `${Math.abs(days)}d ago`;
  if (days === 0) return "Today";
  if (days <= 7)  return `${days}d`;
  if (days <= 60) return `${Math.ceil(days / 7)}w`;
  return `${Math.ceil(days / 30)}mo`;
}

export function fmtDueAbsolute(iso) {
  if (!iso || !isSaneDate(iso)) return null;
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day:   "numeric",
    year:  "numeric",
  });
}

export function dueColor(iso) {
  if (!iso || !isSaneDate(iso)) return T.mid;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(iso) - today) / 86_400_000);
  if (days <= 30) return T.radarIssue;
  return T.mid;
}

// P-61b: use the same subject identity that the drawer's subject card shows.
// The server-enriched name is preferred, but older/partial feed rows still have
// a useful subject id. The final fallback keeps every sentence grammatical.
export function getSubjectDisplayName(item) {
  const candidates = [
    item?.subject?.name,
    item?.locationName,
    item?.subject_name,
    item?.subject_id,
  ];
  const name = candidates
    .map((value) => value == null ? "" : String(value).trim())
    .find(Boolean);
  return name || "this item";
}

export function whySurfaced(item, subjectName = null) {
  const days = daysUntilClient(item.dueDate);

  // The original compact-card copy remains unchanged when no subject is
  // supplied. The mounted /radar drawer passes the resolved subject so its
  // why block always explains what the item is about.
  if (subjectName) {
    const subject = String(subjectName).trim() || "this item";
    switch (item.type) {
      case "expiry":
        if (days !== null && days <= 30) return `${subject}'s lease expires in ${days}d and needs immediate action.`;
        if (days !== null && days <= 90) return `${subject}'s lease expires within ${days}d and needs a plan.`;
        return `${subject} has an approaching lease critical date.`;
      case "decision":
        if (days !== null && days < 0) return `${subject} has a decision deadline that passed ${Math.abs(days)}d ago.`;
        if (days !== null && days <= 14) return `${subject} has a decision deadline in ${days}d and needs a binding choice.`;
        if (days !== null && days <= 30) return `${subject} has a decision due in ${days}d and needs leadership sign-off.`;
        return `${subject} has an open decision with an upcoming deadline.`;
      case "action":
        if (days !== null && days < 0) return `${subject} has an overdue action that needs attention.`;
        if (days !== null && days <= 14) return `${subject} has an action due in ${days}d and assigned to this portfolio.`;
        return `${subject} has an open action with a deadline approaching.`;
      case "issue":
        return `${subject} has a portfolio issue flagged for leadership review.`;
      case "signal":
        return `${subject} has a signal that requires triage and may affect space or people data.`;
      case "project":
        return `${subject} has a project milestone approaching and needs next steps confirmed.`;
      case "review":
        return `${subject} is due for a portfolio review to keep its planning data current.`;
      default:
        return `${subject} was surfaced based on its due date and importance score.`;
    }
  }

  switch (item.type) {
    case "expiry":
      if (days !== null && days <= 30) return `Expiration in ${days}d · requires immediate action`;
      if (days !== null && days <= 90) return `Expiration within ${days}d · high importance score`;
      return "Lease critical date approaching";
    case "decision":
      if (days !== null && days <= 14) return `Decision deadline in ${days}d - binding choice required`;
      if (days !== null && days <= 30) return `Decision due in ${days}d · needs leadership sign-off`;
      return "Open decision with upcoming deadline";
    case "action":
      if (days !== null && days < 0) return "Action item is overdue";
      if (days !== null && days <= 14) return `Action due in ${days}d · assigned to this portfolio`;
      return "Open action item with deadline approaching";
    case "signal":
      return "Signal requires triage - potential impact on space or people data";
    case "issue":
      return "Portfolio issue flagged for leadership review";
    case "project":
      return "Project milestone approaching - confirm next steps to avoid slippage";
    default:
      return "Surfaced based on due date and importance score";
  }
}
